// apps/share-web/src/chart/chart-controls.ts
//
// Дропдауны вместо степперов (решение Сергея): тональность это 15 вариантов, до
// нужного степпером надо жать семь раз, а на телефоне <select> отдаёт выбор
// родному пикеру системы. Подписей «тональность»/«колонки» над ними нет —
// значение говорит само, и шапка получает вдвое больше места. Визуальная подпись
// ушла, доступное имя осталось: <select> без имени скринридер объявит как
// «список», и всё.
//
// Имя тональности считает ЯДРО (@bandway/chordpro), а не своя таблица полутонов,
// и это не стилистика. Черновик плана искал тональность в собственном списке из
// 12 мажоров со смешанным написанием (Eb, Ab, Bb — но C#, F#) через indexOf. На
// домене, который десктоп реально пишет в `song.key`
// (app/renderer/ui/key-picker.js — 12 мажоров и 12 миноров, все с диезами),
// такой список не находит 15 значений из 24: все миноры (Am, Em, F#m — половина
// worship-репертуара) и мажоры D#, G#, A#. Каждое уезжало в ветку «тональность
// неизвестна» и вместо имени показывало «+2» — при известной тональности песни.
// Ядро разбирает их все, помнит бемольность (Bb не превращается в A#) и печатает
// аккорд ровно так же, как напечатает лист рядом, — то есть подпись дропдауна и
// аккорды на листе не могут разойтись.

import { parseChord, renderChord } from '@bandway/chordpro';
import { clampFontPct, fontPctLabel, FONT_PCT_STEP, FONT_PCT_MIN, FONT_PCT_MAX } from './chart-font.js';
import { iconButton, textButton } from './chart-buttons.js';
import { t } from '../ui/i18n.js';

// Та же нотация, что у листа (chart-render.ts, там же причина): настроек
// публикатора в шаре нет, читаем по-английски.
const NOTATION = 'B' as const;

export const SHIFT_MIN = -7;
export const SHIFT_MAX = 7;
/** Три колонки — потолок вёрстки; сколько из них разрешено, решает экран. */
export const COLUMNS_MAX = 3;

/**
 * Целое из чего угодно. `x | 0` из черновика плана давал ноль на `undefined` и
 * `NaN` — верный ответ, но молча, и с двумя побочными правилами: побитовое ИЛИ
 * обрезает дробь к нулю (−2.7 → −2) и заворачивает всё за пределами int32.
 * Лист ряду (chart-render.ts) округляет. Два разных правила на одном числе —
 * это сдвиг 2 в дропдауне против сдвига 3 на листе.
 */
function toInt(v: unknown, fallback: number): number {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n) : fallback;
}

const clamp = (n: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, n));

/**
 * Сдвиг, годный и для подписи, и для листа. Экспортируется потому, что владелец
 * значений живёт в player-view/chart-surfaces.ts и отдаёт их поверхностям
 * начальными: дропдаун свои числа прижимает сам, а `renderChart` — нет, и поверхность, нарисовавшая по
 * НЕприжатому числу, показала бы аккорды не той тональности, что подпись рядом.
 * Одно правило на одно число — то же соображение, что у `toInt` выше.
 */
export const clampShift = (v: unknown): number => clamp(toInt(v, 0), SHIFT_MIN, SHIFT_MAX);

/** Колонки в пределах потолка ЭТОГО экрана (2 на телефоне, 3 на широком). */
export const clampColumns = (v: unknown, max: number): number =>
  clamp(toInt(v, 1), 1, clamp(toInt(max, 1), 1, COLUMNS_MAX));

/** Сдвиг со знаком: «+2», «-1», «0». */
const signed = (n: number): string => (n > 0 ? `+${n}` : String(n));

/**
 * Создаёт дропдаун внутри host — или ЗАМЕНЯЕТ НА МЕСТЕ свой прошлый, если он там
 * уже есть. Повторный монтаж это норма: сменилась песня, сменилась её
 * тональность в манифесте, подписи надо пересчитать. Без замены в шапке
 * оказалось бы два дропдауна, из которых слушает только последний.
 *
 * Именно replaceChild, а не «снять и приписать в конец»: в хосте рядом живёт
 * второй дропдаун, и приписывание меняло бы их порядок — на глазах у
 * пользователя тональность и колонки менялись бы местами при каждой смене
 * песни. И ищем ровно свой (по классу) среди прямых детей, а не чистим хост.
 */
function wrap(host: HTMLElement, cls: string, ariaLabel: string): HTMLSelectElement {
  const box = document.createElement('div');
  box.className = `cw-dd ${cls}`;
  const sel = document.createElement('select');
  sel.setAttribute('aria-label', ariaLabel);
  box.appendChild(sel);

  const prev = [...host.children].find((kid) => kid.classList.contains(cls));
  if (prev) host.replaceChild(box, prev);
  else host.appendChild(box);
  return sel;
}

export interface KeySelectOpts {
  /** Тональность песни из манифеста. Пустая или неразборная — не ошибка. */
  songKey: string;
  shift: number;
  onChange: (shift: number) => void;
}

export function mountKeySelect(host: HTMLElement, opts: KeySelectOpts): void {
  const sel = wrap(host, 'cw-dd-key', t('chart.keyLabel'));
  // Разбираем ОДИН раз: 15 подписей — 15 транспозиций одного разобранного
  // аккорда, а не 15 разборов одной строки.
  const base = parseChord(opts.songKey ?? '', NOTATION);

  for (let sh = SHIFT_MIN; sh <= SHIFT_MAX; sh++) {
    const o = document.createElement('option');
    o.value = String(sh);
    const name = base ? renderChord(base, sh, NOTATION) : null;
    if (name == null) {
      // Имени нет — печатаем сам сдвиг: врать тональностью, которой у песни
      // нет, хуже честного «+2». Ноль всё равно помечаем словом: «вернуть как
      // было» ищут в этом списке первым, а незаполненная тональность в
      // манифесте — обычное дело, не сбой, и опора нужна и там.
      o.textContent = sh === 0 ? t('chart.keyOriginalNoKey') : signed(sh);
    } else {
      o.textContent = sh === 0
        ? t('chart.keyOriginal', { key: name })
        : `${name} ${signed(sh)}`;
    }
    sel.appendChild(o);
  }

  sel.value = String(clampShift(opts.shift));
  // Наружу едет ЧИСЛО, и оно в диапазоне. `Number(sel.value)` на сбитом селекте
  // даёт `Number('') === 0`: браузер (и happy-dom) на присвоение
  // несуществующего значения ставит value='' и selectedIndex=−1.
  sel.addEventListener('change', () => {
    opts.onChange(clampShift(sel.value));
  });
}

export interface ColumnsSelectOpts {
  /** Потолок: 3 на широком экране, 2 на телефоне — три там не читаются. */
  max: number;
  columns: number;
  onChange: (columns: number) => void;
}

export function mountColumnsSelect(host: HTMLElement, opts: ColumnsSelectOpts): void {
  const sel = wrap(host, 'cw-dd-cols', t('chart.columnsLabel'));
  // Мусор в потолке прижимается к одной колонке, а не к трём: одна читается на
  // любом экране, три на телефоне — нет.
  const max = clamp(toInt(opts.max, 1), 1, COLUMNS_MAX);

  for (let n = 1; n <= max; n++) {
    const o = document.createElement('option');
    o.value = String(n);
    o.textContent = n === 1 ? t('chart.columns', { n }) : t('chart.columnsPlural', { n });
    sel.appendChild(o);
  }

  sel.value = String(clampColumns(opts.columns, max));
  // Нуль колонок наружу — это пустой лист, поэтому нижняя граница здесь 1, а не
  // 0: см. про `Number('')` выше.
  sel.addEventListener('change', () => {
    opts.onChange(clampColumns(sel.value, max));
  });
}

export interface FontStepperOpts {
  pct: number;
  onChange: (pct: number) => void;
}

/**
 * Степпер размера: `−` · «+20%» · `+` · Reset. Words page v2 (14.09): Сергей —
 * «текст сайз не был ограничен туда-сюда, а больше можно было сколько угодно
 * накручивать». Дропдаун из шести пунктов этого не умел; степпер идёт по 5 % в
 * пределах технического диапазона chart-font.ts, крайние кнопки гаснут на
 * границе. Кнопки — ДС (`bw-button`), подпись — `<output>`, живая область для
 * скринридера. Повторный монтаж заменяет свой прошлый НА МЕСТЕ (как `wrap`).
 */
export function mountFontStepper(host: HTMLElement, opts: FontStepperOpts): void {
  const box = document.createElement('div');
  box.className = 'cw-stepper cw-stepper-font';
  box.setAttribute('role', 'group');
  box.setAttribute('aria-label', t('chart.fontLabel'));

  let pct = clampFontPct(opts.pct);
  const out = document.createElement('output');
  out.className = 'cw-stepper-val';
  out.setAttribute('aria-live', 'polite');

  const dec = iconButton({ glyph: '−', name: t('chart.fontSmaller'), cls: 'cw-stepper-dec', size: 'md' });
  const inc = iconButton({ glyph: '+', name: t('chart.fontLarger'), cls: 'cw-stepper-inc', size: 'md' });
  const rst = textButton({ text: t('chart.fontReset'), cls: 'cw-stepper-reset', size: 'sm' });
  dec.dataset.role = 'dec'; inc.dataset.role = 'inc'; rst.dataset.role = 'reset';

  const render = (): void => {
    out.textContent = fontPctLabel(pct);
    dec.disabled = pct <= FONT_PCT_MIN;
    inc.disabled = pct >= FONT_PCT_MAX;
    rst.disabled = pct === 0;
  };
  const set = (next: number): void => {
    const v = clampFontPct(next);
    if (v === pct) return;
    pct = v;
    render();
    opts.onChange(pct);
  };
  dec.addEventListener('bw-click', () => set(pct - FONT_PCT_STEP));
  inc.addEventListener('bw-click', () => set(pct + FONT_PCT_STEP));
  rst.addEventListener('bw-click', () => set(0));
  render();

  box.append(dec, out, inc, rst);
  const prev = [...host.children].find((kid) => kid.classList.contains('cw-stepper-font'));
  if (prev) host.replaceChild(box, prev);
  else host.appendChild(box);
}
